/**
 * Simulation core: one tick moves the bikes, resolves collisions, handles
 * bonuses, runs the AI, and manages round state and scores.
 *
 * Ported from `JeuEvolue` (`Jeu.c:269`) and the round/match logic of
 * `SDLBoucleJeu` (`SDL.c:437`).
 */

import { bikeCollision, bikesOverlap, CollisionResult, type GridBounds } from "./collision";
import { makeBike, stepBike, turnBike, type Bike } from "./bike";
import { ageSegments, removeDeadSegments, type Segment } from "./trail";
import { bikeTouchingBonus, placeBonus, type Bonus } from "./bonus";
import { Rng } from "./rng";
import type { PlayerColor, TurnInput } from "./types";
import { BonusKind, PlayerState } from "./types";
import {
  COLOR_FOR_PLAYER,
  DEFAULT_CONFIG,
  spawnPoints,
  BIKE_SHORT,
  BIKE_LONG,
  type GameConfig,
} from "../game/config";
import { computeAiMove, makeAiScratch, type AiScratch } from "../ai/brain";
import { AI_LEVELS, type AiLevel, type AiLevelName } from "../ai/levels";

export interface PlayerInput {
  /** Turn requested this tick (absolute direction), or `null`. */
  turn: TurnInput | null;
  /** Bonus key pressed this tick (rising edge). */
  useBonus: boolean;
}

export const NO_INPUT: PlayerInput = { turn: null, useBonus: false };

export interface Player {
  index: number;
  color: PlayerColor;
  bike: Bike;
  state: PlayerState;
  /** Computer-controlled (fills the slots past `humanCount`). */
  isAI: boolean;
  /** Held/active bonus effect (`effetActuel`). CLEAN is instant so never lands here. */
  effect: BonusKind;
  /** Bonus countdown (`tempsBonus`). Equal to the kind's duration while armed, decreasing once triggered. */
  bonusTimer: number;
}

export type MatchPhase = "playing" | "roundOver" | "gameOver";

export interface MatchEvent {
  text: string;
  color: PlayerColor | null;
}

/** Typed one-off events for a single tick, drained by the presentation layer. */
export type SimEvent =
  | { kind: "death"; player: number; x: number; y: number; color: PlayerColor }
  | { kind: "bonus"; player: number; effect: BonusKind; color: PlayerColor }
  | { kind: "clean"; player: number; color: PlayerColor }
  | { kind: "roundEnd"; winner: number | null };

export interface MatchOptions {
  playerCount: number;
  /** First `humanCount` players are human; the rest are AI. Defaults to all human. */
  humanCount?: number;
  aiLevel?: AiLevelName;
  seed?: number;
  config?: GameConfig;
}

export class Match {
  readonly config: GameConfig;
  readonly grid: GridBounds;
  readonly playerCount: number;
  readonly humanCount: number;
  readonly aiLevel: AiLevel;

  players: Player[] = [];
  segments: Segment[] = [];
  bonuses: Bonus[] = [];
  scores: number[];
  phase: MatchPhase = "playing";
  tickCount = 0;
  round = 1;
  /** Bumped whenever the trail is wiped (CLEAN bonus) so the renderer can drop its path cache. */
  trailGeneration = 0;
  /** Latest info message (death, win…), for the HUD. */
  lastEvent: MatchEvent | null = null;
  /** Typed events from the most recent `tick()` — reset at the start of each tick. */
  frameEvents: SimEvent[] = [];
  /** Index of the round winner, or `null` (draw / match in progress). */
  roundWinner: number | null = null;
  /** Index of the match winner, set when `phase === 'gameOver'`. */
  matchWinner: number | null = null;

  private rng: Rng;
  private nextBonusIn = 0;
  private readonly baseSeed: number;
  private readonly aiScratch: AiScratch;

  constructor(opts: MatchOptions) {
    this.config = opts.config ?? DEFAULT_CONFIG;
    this.grid = { ...this.config.grid };
    this.playerCount = opts.playerCount;
    this.humanCount = Math.min(opts.humanCount ?? opts.playerCount, opts.playerCount);
    this.aiLevel = AI_LEVELS[opts.aiLevel ?? "normal"];
    this.aiScratch = makeAiScratch(this.grid);
    this.baseSeed = opts.seed ?? 0x1337;
    this.rng = new Rng(this.baseSeed);
    this.scores = Array.from({ length: this.playerCount }, () => 0);
    this.startRound();
  }

  /** (Re)initialises the current round; keeps the scores. */
  startRound(): void {
    const spawns = spawnPoints(this.playerCount);
    this.players = spawns.map((s, i) => {
      const vertical = s.dir === 1 || s.dir === 2;
      const w = vertical ? BIKE_SHORT : BIKE_LONG;
      const h = vertical ? BIKE_LONG : BIKE_SHORT;
      const color = COLOR_FOR_PLAYER[i]!;
      return {
        index: i,
        color,
        state: PlayerState.Alive,
        isAI: i >= this.humanCount,
        effect: BonusKind.None,
        bonusTimer: 0,
        bike: makeBike(
          this.grid.x + s.x,
          this.grid.y + s.y,
          w,
          h,
          this.config.initialSpeed,
          s.dir,
          color,
        ),
      };
    });
    this.segments = [];
    this.bonuses = [];
    this.tickCount = 0;
    this.trailGeneration += 1;
    this.phase = "playing";
    this.lastEvent = null;
    this.roundWinner = null;
    // Fresh per-round random stream derived from the base seed.
    this.rng = new Rng((this.baseSeed ^ Math.imul(this.round, 0x9e3779b9)) >>> 0);
    this.nextBonusIn = this.rng.int(this.config.bonus.respawnFirst); // C: rand() % 150
  }

  /** Advances to the next round (or ends the match if a score reached the threshold). */
  nextRound(): void {
    const winner = this.decideMatchWinner();
    if (winner !== undefined) {
      this.phase = "gameOver";
      this.matchWinner = winner;
      return;
    }
    this.round += 1;
    this.startRound();
  }

  /** Zeroes the scores and restarts a match. */
  resetMatch(): void {
    this.scores.fill(0);
    this.round = 1;
    this.matchWinner = null;
    this.startRound();
  }

  /** True once a score has reached the victory threshold with no tie at the top. */
  isDecided(): boolean {
    return this.decideMatchWinner() !== undefined;
  }

  private decideMatchWinner(): number | undefined {
    let best: number | undefined;
    let bestScore = 0;
    let tie = false;
    for (let i = 0; i < this.playerCount; i++) {
      const s = this.scores[i]!;
      if (s < this.config.victoryScore) continue;
      if (best === undefined || s > bestScore) {
        best = i;
        bestScore = s;
        tie = false;
      } else if (s === bestScore) {
        tie = true;
      }
    }
    return tie ? undefined : best;
  }

  private countInPlay(): number {
    let n = 0;
    for (const p of this.players) {
      if (p.state === PlayerState.Alive || p.state === PlayerState.Doubt) n += 1;
    }
    return n;
  }

  /** JUMP is phasing through trails right now (armed doesn't count). */
  private isJumping(p: Player): boolean {
    return p.effect === BonusKind.Jump && p.bonusTimer !== this.config.bonus.jumpDuration;
  }

  /** One simulation step. `inputs[i]` is the input for player index i. */
  tick(inputs: readonly PlayerInput[]): void {
    if (this.phase !== "playing") return;
    this.tickCount += 1;
    this.frameEvents = [];

    // Snapshot positions for render interpolation.
    for (const p of this.players) {
      p.bike.px = p.bike.x;
      p.bike.py = p.bike.y;
    }

    const effective = this.resolveInputs(inputs);

    // 1. Turns and bonus triggers (in the C these fire from the SDL event loop
    //    before JeuEvolue; the AI ran inside `JeuGereIA`). The corner segment
    //    goes into the trail (`ajouteMur`).
    for (const p of this.players) {
      if (p.state !== PlayerState.Alive) continue;
      const input = effective[p.index] ?? NO_INPUT;
      if (input.turn !== null) {
        const corner = turnBike(p.bike, input.turn, this.config.trailLife);
        if (corner) this.segments.push(corner);
      }
      if (input.useBonus) this.triggerBonus(p);
    }

    // 2. Movement + trail drop (ex-`bougeMoto`).
    for (const p of this.players) {
      if (p.state !== PlayerState.Alive) continue;
      const seg = stepBike(p.bike, this.config.acceleration, this.config.trailLife);
      this.segments.push(seg);
      p.bike.lastSegment = seg;
    }

    // 3. Bike-wall / edge / bike-bike collisions.
    for (let i = 0; i < this.players.length; i++) {
      const p = this.players[i]!;
      if (p.state !== PlayerState.Alive) continue;
      const jumping = this.isJumping(p);

      const res = bikeCollision(p.bike, this.grid, this.segments, jumping);
      if (res === CollisionResult.Hit) {
        p.state = PlayerState.Dying;
        this.lastEvent = { text: `Player ${i + 1} eliminated`, color: p.color };
        continue;
      }
      if (res === CollisionResult.Graze) {
        p.state = PlayerState.Doubt;
      }
      if (jumping) continue;
      for (let j = i + 1; j < this.players.length; j++) {
        const q = this.players[j]!;
        if (q.state === PlayerState.Alive && !this.isJumping(q) && bikesOverlap(p.bike, q.bike)) {
          p.state = PlayerState.Dying;
          q.state = PlayerState.Dying;
          this.lastEvent = {
            text: `Head-on: players ${i + 1} and ${j + 1}`,
            color: null,
          };
        }
      }
    }

    // 4. DOUBT state resolution (ex-`Jeu.c:326-354`).
    let inPlay = this.countInPlay();
    const doubters = this.players.filter((p) => p.state === PlayerState.Doubt);

    if (doubters.length === 1) {
      const d = doubters[0]!;
      d.state = PlayerState.Dying;
      this.lastEvent = { text: `Player ${d.index + 1} eliminated`, color: d.color };
      inPlay -= 1;
    } else if (doubters.length > 1) {
      for (const d of doubters) {
        if (d.bike.turnedThisTick) {
          d.state = PlayerState.Dying;
          this.lastEvent = { text: `Player ${d.index + 1} eliminated`, color: d.color };
          inPlay -= 1;
        } else {
          d.state = PlayerState.Alive;
        }
      }
    }

    // 5. Round end. With 2+ players the round ends at one survivor (`NbJoueurEnJeu==1`
    //    in the C original); a solo round only ends once that player is out.
    const endThreshold = this.playerCount <= 1 ? 0 : 1;
    if (inPlay <= endThreshold) {
      const survivor = this.players.find((p) => p.state === PlayerState.Alive);
      if (survivor) {
        this.scores[survivor.index]! += 2;
        this.roundWinner = survivor.index;
        this.lastEvent = {
          text: `Player ${survivor.index + 1} wins the round`,
          color: survivor.color,
        };
      } else {
        for (const p of this.players) {
          if (p.state === PlayerState.Dying) this.scores[p.index]! += 1;
        }
        this.roundWinner = null;
        this.lastEvent = { text: "Draw", color: null };
      }
      this.phase = "roundOver";
      this.frameEvents.push({ kind: "roundEnd", winner: this.roundWinner });
    }

    // 6. DYING -> DEAD, reset `turnedThisTick`.
    for (const p of this.players) {
      if (p.state === PlayerState.Dying) {
        p.state = PlayerState.Dead;
        this.frameEvents.push({
          kind: "death",
          player: p.index,
          x: p.bike.x + p.bike.w / 2,
          y: p.bike.y + p.bike.h / 2,
          color: p.color,
        });
      } else {
        p.bike.turnedThisTick = false;
      }
    }

    // 7. Bonuses: spawn, pickup, then age the active timers.
    this.updateBonuses();
    this.ageBonusTimers();

    // 8. Age and clean up trail segments.
    ageSegments(this.segments);
    removeDeadSegments(this.segments);
  }

  /** Merge human inputs with freshly computed AI moves (ex-`JeuGereIA`). */
  private resolveInputs(inputs: readonly PlayerInput[]): PlayerInput[] {
    const level = this.aiLevel;
    return this.players.map((p) => {
      if (!p.isAI || p.state !== PlayerState.Alive) return inputs[p.index] ?? NO_INPUT;
      // Reaction lag (easy mode): only recompute on this AI's react tick, else coast.
      if ((this.tickCount + p.index) % level.reactEvery !== 0) return NO_INPUT;
      return computeAiMove(this, p.index, level, this.aiScratch, () => this.rng.next());
    });
  }

  /** `JeuActionneBonus`: trigger a held BOOST/JUMP. Only fires when still armed. */
  private triggerBonus(p: Player): void {
    const { boostSpeed, boostDuration, jumpDuration } = this.config.bonus;
    if (p.effect === BonusKind.Boost && p.bonusTimer === boostDuration) {
      p.bonusTimer -= 1;
      p.bike.speed += boostSpeed;
    } else if (p.effect === BonusKind.Jump && p.bonusTimer === jumpDuration) {
      p.bonusTimer -= 1;
    }
  }

  /** Spawn on the `nextBonusIn` timer, then hand out any bonus a bike is touching. */
  private updateBonuses(): void {
    const cfg = this.config.bonus;

    if (this.nextBonusIn <= 0) {
      if (this.bonuses.length < cfg.maxOnGrid) {
        const bikes = this.players.filter((p) => p.state !== PlayerState.Dead).map((p) => p.bike);
        const b = placeBonus(this.grid, cfg.size, bikes, this.rng);
        if (b) this.bonuses.push(b);
      }
      this.nextBonusIn = this.rng.range(cfg.respawnMin, cfg.respawnMax);
    } else {
      this.nextBonusIn -= 1;
    }

    const alive = this.players.filter((p) => p.state === PlayerState.Alive);
    const aliveBikes = alive.map((p) => p.bike);
    for (let bi = this.bonuses.length - 1; bi >= 0; bi--) {
      const b = this.bonuses[bi]!;
      const hit = bikeTouchingBonus(b, aliveBikes);
      if (hit === -1) continue;
      const p = alive[hit]!;
      const taker = p.index;

      if (b.kind === BonusKind.Clean) {
        this.segments.length = 0;
        this.trailGeneration += 1;
        for (const other of this.players) other.bike.lastSegment = null;
        this.lastEvent = { text: `Player ${taker + 1} wiped the grid`, color: p.color };
        this.frameEvents.push({ kind: "clean", player: taker, color: p.color });
      } else if (b.kind === BonusKind.Boost) {
        p.effect = BonusKind.Boost;
        p.bonusTimer = cfg.boostDuration;
        this.lastEvent = { text: `Player ${taker + 1} armed BOOST`, color: p.color };
        this.frameEvents.push({
          kind: "bonus",
          player: taker,
          effect: BonusKind.Boost,
          color: p.color,
        });
      } else {
        p.effect = BonusKind.Jump;
        p.bonusTimer = cfg.jumpDuration;
        this.lastEvent = { text: `Player ${taker + 1} armed JUMP`, color: p.color };
        this.frameEvents.push({
          kind: "bonus",
          player: taker,
          effect: BonusKind.Jump,
          color: p.color,
        });
      }
      this.bonuses.splice(bi, 1);
    }
  }

  /** `decrementeTempsBonus`: count down triggered effects and expire them. */
  private ageBonusTimers(): void {
    const { boostSpeed, boostDuration, jumpDuration } = this.config.bonus;
    for (const p of this.players) {
      if (p.effect === BonusKind.Boost && p.bonusTimer !== boostDuration) {
        p.bonusTimer -= 1;
        if (p.bonusTimer <= 0) {
          p.effect = BonusKind.None;
          p.bike.speed -= boostSpeed;
        }
      } else if (p.effect === BonusKind.Jump && p.bonusTimer !== jumpDuration) {
        p.bonusTimer -= 1;
        if (p.bonusTimer <= 0) p.effect = BonusKind.None;
      }
    }
  }

  /** Serialises the state — for replay tests / debug. */
  serialize(): string {
    return JSON.stringify({
      phase: this.phase,
      tick: this.tickCount,
      scores: this.scores,
      rng: this.rng.snapshot(),
      bonuses: this.bonuses.length,
      players: this.players.map((p) => ({
        s: p.state,
        x: Math.round(p.bike.x * 100) / 100,
        y: Math.round(p.bike.y * 100) / 100,
        d: p.bike.dir,
        e: p.effect,
        t: p.bonusTimer,
      })),
      segCount: this.segments.length,
    });
  }
}
